import { partition } from 'lodash';
import { Channel, User } from '../../type';
import { useCareTeamSelector } from './useCareTeamSelector';
import { useDeepEqualMemo } from '@xolv-mybrightlink/common';
import { useCallback } from 'react';

const catalight = 'Catalight';
export type ByProvider = { provider: string; channelGuids: string[] };

export const useCareTeamChannelGuids = (
  patientGuid: string | undefined,
): { active: ByProvider[]; readOnly: ByProvider[] } => {
  const groupChannelByProvider = useCallback(
    (channels: Channel[]): ByProvider[] => {
      const grouped = channels
        .filter((channel) => channel.type !== 'PEER_TO_PEER')
        .reduce((result, channel) => {
          if (channel.attributes.patientGuid === patientGuid) {
            const provider = channel.attributes.provider ?? catalight;
            const channels = result[provider] ?? [];
            channels.push(channel);
            result[provider] = channels;
          }
          return result;
        }, {} as { [provider: string]: Channel[] });
      return Object.keys(grouped)
        .map((provider) => ({
          provider,
          channelGuids: grouped[provider]
            .sort(sortChannels)
            .map((channel) => channel.guid),
        }))
        .sort(sortProvides);
    },
    [patientGuid],
  );
  return useCareTeamSelector(
    useCallback(
      (state) => {
        if (state === 'LOADING') {
          return { active: [], readOnly: [] };
        }
        const [activeChannels, readOnlyChannels] = partition(
          Object.values(state.channels),
          (channel) => !channel.readonly,
        );
        return {
          active: groupChannelByProvider(activeChannels),
          readOnly: groupChannelByProvider(readOnlyChannels),
        };
      },
      [groupChannelByProvider],
    ),
  );
};

export const useChannelMembers = (channelGuid: string): User[] => {
  return useCareTeamSelector(
    useCallback(
      (state) => {
        if (state === 'LOADING') {
          return [];
        }
        const memberGuids = state.channels[channelGuid]?._memberGuids ?? [];
        return memberGuids
          .map((memberGuid) => state.users[memberGuid])
          .filter((user) => user);
      },
      [channelGuid],
    ),
  );
};

export const useChannel = (channelGuid: string): Channel | null => {
  const channel = useCareTeamSelector(
    useCallback(
      (state) => {
        return state === 'LOADING' ? null : state.channels[channelGuid] ?? null;
      },
      [channelGuid],
    ),
  );
  return useDeepEqualMemo(channel);
};

export const useExistChannel = (channelGuid: string): Channel => {
  const channel = useCareTeamSelector(
    useCallback(
      (state) => {
        return state === 'LOADING' ? null : state.channels[channelGuid] ?? null;
      },
      [channelGuid],
    ),
  );
  const memoChannel = useDeepEqualMemo(channel);
  if (!memoChannel) {
    throw new Error(`Channel does not exist: ${channelGuid}`);
  }
  return memoChannel;
};

export type CommunityChannel = {
  active: Channel[];
  disconnected: Channel[];
};

export const useCommunityChannel = (): CommunityChannel => {
  return useCareTeamSelector((state) => {
    if (state === 'LOADING') {
      return {
        active: [],
        disconnected: [],
      };
    }
    return Object.values(state.channels)
      .filter((channel) => channel.type === 'PEER_TO_PEER')
      .reduce<CommunityChannel>(
        (result, channel) => {
          if (channel.readonly) {
            result.disconnected.push(channel);
          } else {
            result.active.push(channel);
          }
          return result;
        },
        {
          active: [],
          disconnected: [],
        },
      );
  });
};

/*================HELPER FUNCTIONS================*/
const sortProvides = (group1: ByProvider, group2: ByProvider): number => {
  if (group1.provider === catalight) {
    return 1;
  } else if (group2.provider === catalight) {
    return -1;
  }
  return group1.provider.localeCompare(group2.provider);
};

const sortChannels = (channel1: Channel, channel2: Channel): number => {
  const channel1Points = channelPoints(channel1);
  const channel2Points = channelPoints(channel2);
  return channel2Points - channel1Points;
};

const channelPoints = (channel: Channel): number => {
  let points = 0;
  if (channel.groupChannel) {
    points += 1;
  }
  if (channel.type !== 'SCHEDULING_TEAM') {
    points += 2;
  }
  return points;
};
